import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './card';
import { Progress } from './progress';
import { Alert, AlertDescription } from './alert';
import { CheckCircle, XCircle, Upload, Download } from 'lucide-react';

interface ImportExportProgressProps {
  type: 'import' | 'export';
  isActive: boolean;
  progress: number;
  message: string;
  errors?: string[];
}

export function ImportExportProgress({
  type,
  isActive,
  progress,
  message,
  errors = [],
}: ImportExportProgressProps) {
  if (!isActive && progress === 0) {
    return null;
  }

  const Icon = type === 'import' ? Upload : Download;
  const title = type === 'import' ? 'Import Progress' : 'Export Progress';

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Icon className="h-5 w-5" />
          <span>{title}</span>
        </CardTitle>
        <CardDescription>{message}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span>Progress</span>
            <span>{progress}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {progress === 100 && errors.length === 0 && (
          <Alert className="border-green-200 bg-green-50">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">
              {type === 'import' ? 'Import' : 'Export'} completed successfully!
            </AlertDescription>
          </Alert>
        )}

        {errors.length > 0 && (
          <Alert className="border-red-200 bg-red-50">
            <XCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">
              <div className="space-y-1">
                <p className="font-medium">Errors occurred during {type}:</p>
                <ul className="list-disc list-inside space-y-1">
                  {errors.map((error, index) => (
                    <li key={index} className="text-sm">{error}</li>
                  ))}
                </ul>
              </div>
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}